import { Box, Typography } from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import Button from '../../../../components/Button/Button';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';
import Dropdown from '../../../../components/Dropdown/Dropdown';
import { DropdownItem } from '../../../../components/Dropdown/DropdownModels';
import TextInput from '../../../../components/TextInput/TextInput';
import useHttpError from '../../../../hooks/useHttpError/useHttpError';
import { Device } from '../../../../models/UserDevicesModels';
import { User } from '../../../../models/UsersModels';
import UsersService from '../../../../services/Users/UsersService';
import { setDevice, updateRequest } from '../../../../stores/NewConnectionStore';
import { RootState } from '../../../../stores/Store';

interface NewConnectionDevicesStepProps {
  availableDevices: Device[];
  nextStep: () => void;
  onClose: () => void;
  isLoading: boolean;
}
function NewConnectionDevicesStep({ availableDevices, nextStep, onClose, isLoading }: NewConnectionDevicesStepProps) {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { eid, userId } = useSelector((state: RootState) => state.newConnectionReducer.request);
  const { userAlreadyExists, userSelectionType, newUser, selectedDropdownUser } = useSelector(
    (state: RootState) => state.newConnectionReducer,
  );
  const typeOfConnection = useSelector((state: RootState) => state.newConnectionReducer.typeOfConnection);
  const [isValidating, setIsValidating] = useState(false);
  const [eSimOwner, setESimOwner] = useState<User | null | undefined>(null);
  const [eSimDevice, setESimDevice] = useState<Device | null | undefined>(null);
  const [showNextStep, setShowNextStep] = useState<boolean>(false);
  const { getHttpReasonFromList } = useHttpError();

  const formik = useFormik({
    initialValues: { eid },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: yup.object({
      eid: yup
        .string()
        .transform((value) => value.trim())
        .required(t('common.inputValidations.requiredField'))
        .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      /* .max(32, t('common.inputValidations.maxLength', { len: 32 }))
        .min(32, t('common.inputValidations.maxLength', { len: 32 })),
        .test('isValidESimID', t('common.inputValidations.invalidESim'), (value) => {
          const decimalValue = parseInt(value, 16);
          const remainder = decimalValue % 97;
          return remainder === 1;
        }), */
    }),
    onSubmit: () => {
      setIsValidating(true);
      const userBan =
        userAlreadyExists || userSelectionType === 'USE_EXISTING'
          ? (selectedDropdownUser.data as User).billingAccountBan
          : newUser?.billingAccountBan;
      UsersService.api.fetchDeviceByEid({ userId, eid, userBan, upgrade: false }).then(
        (response) => {
          setIsValidating(false);
          dispatch(setDevice(response.data.device));
          if (response.data.sim.status !== 'FREE') {
            formik.setErrors({
              eid: t('pages.eSimManager.newConnectionDrawer.eSimAlreadyInUse'),
            });
          } else {
            setShowNextStep(true);
          }
          setESimDevice(response.data.device);
          setESimOwner(response.data.user);
        },
        (res) => {
          formik.setErrors({
            eid: getHttpReasonFromList(
              res.response.data,
              [3104, 3107, 3108],
              t('pages.eSimManager.newConnectionDrawer.validateEidError'),
            ),
          });
          setIsValidating(false);
        },
      );
    },
  });

  /**
   * Dropdown options - devices list
   */
  const getDevicesOptions = useCallback(() => {
    const array: DropdownItem[] = [];
    availableDevices.forEach((d) => {
      if (d.eid !== null) {
        array.push({ value: d.eid, label: `${d.manufacturer} ${d.model}`, key: d.eid });
      }
    });
    return array;
  }, [availableDevices]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {typeOfConnection === 'DEVICE' ? (
        <Box sx={{ paddingBottom: '2rem' }}>
          {!isLoading && getDevicesOptions().length > 0 ? (
            <Dropdown
              id="device"
              name="device"
              label={t('pages.eSimManager.newConnectionDrawer.selectDevice')}
              value={eid}
              onChange={(e) => {
                dispatch(updateRequest({ eid: e.target.value as string }));
                dispatch(setDevice(availableDevices.find((d) => d.eid === (e.target.value as string)) as Device));
              }}
              formStyle={{ paddingBottom: '1.5rem' }}
              items={getDevicesOptions()}
              placeholder={t('pages.eSimManager.newConnectionDrawer.selectDevicePlaceholder')}
            />
          ) : (
            <Typography>{t('pages.eSimManager.newConnectionDrawer.noAvailableDevices')}</Typography>
          )}
          {eid ? (
            <TextInput
              value={eid}
              onInputChange={() => {}}
              disabled
              label={t('pages.eSimManager.newConnectionDrawer.eSimId')}
            />
          ) : null}
        </Box>
      ) : (
        <Box sx={{ paddingBottom: '2rem' }}>
          <form onSubmit={formik.handleSubmit}>
            <TextInput
              id="eid"
              name="eid"
              onInputChange={(e) => {
                setShowNextStep(false);
                setESimDevice(null);
                setESimOwner(null);
                dispatch(updateRequest({ eid: e.target.value }));
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.eid && Boolean(formik.errors.eid)}
              feedback={formik.touched.eid && Boolean(formik.errors.eid) ? formik.errors.eid : ''}
              value={formik.values.eid}
              placeholder={t('pages.eSimManager.newConnectionDrawer.eSimIdPlaceholder')}
              label={t('pages.eSimManager.newConnectionDrawer.eSimId')}
              formStyle={{ paddingBottom: '1.5rem' }}
            />
            {eSimDevice ? (
              <TextInput
                id="device"
                name="device"
                onInputChange={() => {}}
                value={`${eSimDevice.manufacturer} ${eSimDevice.model}`}
                placeholder={t('pages.eSimManager.newConnectionDrawer.device')}
                label={t('pages.eSimManager.newConnectionDrawer.device')}
                disabled
                formStyle={{ paddingBottom: '1.5rem' }}
              />
            ) : null}
            {eSimOwner ? (
              <>
                <TextInput
                  id="user-name"
                  name="user-name"
                  onInputChange={() => {}}
                  value={`${eSimOwner.firstName} ${eSimOwner.lastName}`}
                  placeholder={t('pages.eSimManager.newConnectionDrawer.userName')}
                  label={t('pages.eSimManager.newConnectionDrawer.userName')}
                  disabled
                  formStyle={{ paddingBottom: '1.5rem' }}
                />

                <TextInput
                  id="email-address"
                  name="email-address"
                  onInputChange={() => {}}
                  value={eSimOwner.email}
                  placeholder={t('pages.eSimManager.newConnectionDrawer.emailAddress')}
                  label={t('pages.eSimManager.newConnectionDrawer.emailAddress')}
                  disabled
                  formStyle={{ paddingBottom: '1.5rem' }}
                />
              </>
            ) : null}
          </form>
        </Box>
      )}
      <Box sx={{ marginTop: 'auto' }}>
        <ButtonGroup>
          <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} />
          {typeOfConnection === 'DEVICE' ? (
            <Button label={t('common.continue')} color="primaryButton" disabled={!eid} onClick={nextStep} />
          ) : (
            <Box>
              {showNextStep ? (
                <Button
                  label={t('common.continue')}
                  color="primaryButton"
                  onClick={nextStep}
                  disabled={!formik.isValid}
                />
              ) : (
                <Button
                  label={t('common.validate')}
                  color="primaryButton"
                  disabled={!formik.isValid || isValidating}
                  onClick={formik.submitForm}
                />
              )}
            </Box>
          )}
        </ButtonGroup>
      </Box>
    </Box>
  );
}

export default NewConnectionDevicesStep;
